import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import axios, { getErrorMessage } from 'utils/axios';
import CRUD from 'utils/Crud';
import { useSnackbar } from 'notistack';
import { FormProvider, RHFTextField } from 'components/hook-form';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';

const NewUserSchema = Yup.object().shape({
  status: Yup.string().required('Status is required'),
  password: Yup.string().required('Admin password is required'),
});

ChangeAccountStatus.propTypes = {
  user: PropTypes.object.isRequired,
  status: PropTypes.string, // 'active' or 'suspended'
  onComplete: PropTypes.func,  // Optional: for callback after complete
};

export default function ChangeAccountStatus({ user, status = '', onComplete }) {
  // Use local state for current status
  const [currentStatus, setCurrentStatus] = useState(status);
  const [state, setState] = useState({ loading: false, loadComplete: false, error: null });
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues: {
      status: currentStatus,
      password: '',
    },
  });

  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  const onSubmit = async () => {
    setState({ ...state, loading: true, loadComplete: false, error: null });
    try {
      const payload = {
        user_id: user.user_id,
        status: values.status,
        password: values.password,
      };
      await axios.post(CRUD.user.suspend_user, payload);
      setState({ ...state, loading: false, loadComplete: true, error: null });
      setCurrentStatus(values.status); // <-- Update local status
      reset({ status: values.status, password: '' }); // Reset form, keep new status selected
      enqueueSnackbar('Account status updated successfully!');
      if (onComplete) onComplete(values.status);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error));
      setState({ ...state, loading: false, loadComplete: false });
    }
  };

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, mx: 'auto', mt: 2 }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Change Account Status
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current Status:
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: currentStatus === 'suspended' ? 'error.main' : 'success.main',
              fontWeight: 600,
              mb: 1,
            }}
          >
            {currentStatus === 'suspended' ? 'Suspended' : 'Active'}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Set New Status
          </Typography>
          <RadioGroup
            row
            name="status"
            value={values.status}
            onChange={(e) => setValue('status', e.target.value, { shouldValidate: true })}
            sx={{ mt: 1 }}
          >
            <FormControlLabel
              value="active"
              control={<Radio color="success" />}
              label={<Typography color="success.main">Active</Typography>}
            />
            <FormControlLabel
              value="suspended"
              control={<Radio color="error" />}
              label={<Typography color="error.main">Suspended</Typography>}
            />
          </RadioGroup>
          <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
            Caution: Suspending an account will prevent the user from accessing their account. Only use this action when necessary.
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Admin Password
          </Typography>
          <RHFTextField
            name="password"
            label="Enter your password to confirm"
            type="password"
            required
            sx={{ mt: 1 }}
          />
        </Box>

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
          <LoadingButton
            type="submit"
            variant="contained"
            color={values.status === 'suspended' ? 'error' : 'success'}
            loading={isSubmitting}
          >
            {values.status === 'suspended' ? 'Suspend Account' : 'Set Active'}
          </LoadingButton>
        </Stack>
      </FormProvider>
    </Paper>
  );
}
